import { useTranslation } from 'react-i18next';
import { withStyles, type AccountUserRole } from 'vopay';
import { useApi } from '#shared/providers/Api';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useUsers from '../useUsers';
import unwrap from '../unwrap';
import styles from './DeleteRoleConfirmation.module.scss';

const cn = withStyles(styles);

// Confirms and deletes a role, fanning the delete out to sub-accounts when the role was applied to them.
// Resolves true once the role itself is gone (the sub-account fan-out can still have partly failed, which
// the toast reports), false when the admin backs out or the delete fails.
export default function useDeleteRole() {
  const api = useApi();
  const { account, countSubAccounts, deleteRoleFromSubAccounts } = useUsers();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.DeleteRole' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();

  async function deleteRole(role: AccountUserRole, numUsers: number): Promise<boolean> {
    const propagates =
      role.ApplyToAllSubaccounts && account.hasSubAccounts && !!countSubAccounts && !!deleteRoleFromSubAccounts;

    // The dialog says how many sub-accounts the role is removed from, so count them first.
    let numSubAccounts = 0;

    if (propagates) {
      try {
        numSubAccounts = await countSubAccounts();
      } catch {
        // Already logged; the dialog just shows 0.
      }
    }

    const confirmation = await alertDialog.caution(
      t('title', { defaultValue: 'Delete Role?' }),
      propagates ? (
        <div data-apply-confirm-dialog="delete-role-cascade">
          <p className={cn('confirmDialogLead')}>
            {t('cascadeLead', { defaultValue: 'Are you sure you want to delete this role? This role will be:' })}
          </p>

          <ul className={cn('confirmDialogList')}>
            <li>
              {t('cascadeBullet1', {
                defaultValue: 'Removed from {{numSubAccounts}} sub-accounts',
                numSubAccounts,
              })}
            </li>

            <li>{t('cascadeBullet2', { defaultValue: 'Unassigned from {{numUsers}} users', numUsers })}</li>
          </ul>
        </div>
      ) : (
        <div data-apply-confirm-dialog="delete-role">
          <p className={cn('confirmDialogLead')}>
            {t('body', {
              defaultValue:
                'Are you sure you want to delete this role? This role will be unassigned from {{count}} users.',
              count: numUsers,
            })}
          </p>
        </div>
      ),
      {
        abortLabel: t('goBack', { defaultValue: 'Go Back' }),
        confirmLabel: tButtons('delete', { defaultValue: 'Delete' }),
      },
    );

    if (confirmation !== 'confirm') return false;

    blockingLoader.show();

    try {
      await unwrap(api.deleteAccountUserRole({ RoleID: role.RoleID }, undefined, account.bearer));

      const fanOut = propagates ? await deleteRoleFromSubAccounts(role.Name) : null;

      if (fanOut && (fanOut.failed.length > 0 || fanOut.skipped.length > 0)) {
        toast.neutral(
          t('partial', {
            defaultValue:
              'Role "{{roleName}}" deleted. Removed from {{numDeleted}} of {{numTotal}} sub-accounts ({{numFailed}} failed, {{numSkipped}} skipped).',
            roleName: role.Name,
            numDeleted: fanOut.numDeleted,
            numTotal: fanOut.numTotal,
            numFailed: fanOut.failed.length,
            numSkipped: fanOut.skipped.length,
          }),
        );
      } else {
        toast.success(
          t('success', { defaultValue: 'Role "{{roleName}}" was deleted successfully.', roleName: role.Name }),
        );
      }

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Could not delete role' }), errorMessage);

      return false;
    } finally {
      blockingLoader.hide();
    }
  }

  return { deleteRole };
}
